import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, computed, effect, inject, linkedSignal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { DEFAULT_USER_SETTINGS, RESPONSE_SETTINGS_DEFAULT } from '@common/data';
import { ANONYMOUS_PREFERENCES_OWNER, USER_SETTINGS_KEY_PREFIX } from '@common/enums';
import { reconcileUserSettings } from '@common/functions';
import type { UserSettings, UserSettingsSection } from '@common/types';

import { AccountSettingsService } from './account-settings.service';
import { AuditService } from './audit.service';
import { CurrentUserService } from './current-user.service';
import { DateFilterService } from './date-filter.service';
import { ScopeService } from './scope.service';
import { UserPreferencesService } from './user-preferences.service';

const SAVE_LATENCY_MS = 280;

const SECTION_LABELS: Readonly<Record<UserSettingsSection, string>> = {
  workspace: 'workspace defaults',
  appearance: 'appearance',
  responding: 'responding preferences',
  tickets: 'service ticket preferences',
};

/**
 * The signed-in user's chosen settings - the deliberate choices on `/settings`, as opposed to the
 * state the app remembers from use (`UserPreferencesService`).
 *
 * It persists to localStorage under the user's Cognito `sub` as a stand-in for
 * `GET/PATCH me/settings`; the save methods already return Observables with a failure path, so moving
 * to `ApiService` changes this file and nothing that calls it.
 */
@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly currentUser = inject(CurrentUserService);
  private readonly preferences = inject(UserPreferencesService);

  private readonly owner = computed<string>(
    () => this.currentUser.sub() ?? ANONYMOUS_PREFERENCES_OWNER,
  );

  private readonly state = linkedSignal<string, UserSettings>({
    source: this.owner,
    computation: (owner) => this.load(owner),
  });

  readonly settings = this.state.asReadonly();
  readonly workspace = computed(() => this.state().workspace);
  readonly appearance = computed(() => this.state().appearance);
  readonly responding = computed(() => this.state().responding);
  readonly tickets = computed(() => this.state().tickets);

  /**
   * Where the app opens with no deep link. A pinned dashboard that has since been unpinned or
   * deleted falls back to the user's own dashboard rather than a 404.
   */
  readonly landingUrl = computed<string>(() => {
    const workspace = this.workspace();
    switch (workspace.landingPage) {
      case 'pinned-dashboard': {
        const id = workspace.landingDashboardId;
        const pinned = this.preferences
          .reports()
          .some((r) => r.id === id && r.isFavourite && r.format === 'dashboard');
        return pinned ? `/dashboard/${id}` : '/dashboard';
      }
      case 'reviews':
        return '/reviews';
      case 'tickets':
        return '/service-tickets';
      case 'guests':
        return '/guests';
      case 'reports':
        return '/reports';
      default:
        return '/dashboard';
    }
  });

  private startupAppliedFor: string | null = null;

  /**
   * The sign-off a reply from this person ends with: theirs if they set one, otherwise the
   * account's. With no single account in hand the organisation-wide default stands in.
   */
  effectiveSignature(accountId: string | null): string {
    const own = this.responding().signature;
    if (own !== null) return own;
    return accountId
      ? this.injector.get(AccountSettingsService).responseSettingsFor(accountId).signature
      : RESPONSE_SETTINGS_DEFAULT.signature;
  }

  effectiveTone(accountId: string | null): string {
    const own = this.responding().defaultTone;
    if (own !== null) return own;
    return accountId
      ? this.injector.get(AccountSettingsService).responseSettingsFor(accountId).defaultTone
      : RESPONSE_SETTINGS_DEFAULT.defaultTone;
  }

  constructor() {
    effect(() => {
      const owner = this.owner();
      const data = this.state();
      if (owner === ANONYMOUS_PREFERENCES_OWNER) return;
      try {
        this.document.defaultView?.localStorage.setItem(storageKeyFor(owner), JSON.stringify(data));
      } catch {}
    });
  }

  save<S extends UserSettingsSection>(
    section: S,
    value: UserSettings[S],
  ): Observable<UserSettings> {
    const next: UserSettings = {
      ...this.state(),
      [section]: value,
      updatedAt: new Date().toISOString(),
    };
    // Persist before publishing an explicit save, so quota/permission errors reach the caller.
    try {
      const owner = this.owner();
      if (owner !== ANONYMOUS_PREFERENCES_OWNER) {
        const storage = this.document.defaultView?.localStorage;
        if (!storage) throw new Error('Settings storage is unavailable');
        storage.setItem(storageKeyFor(owner), JSON.stringify(next));
      }
    } catch (error) {
      return throwError(() => error);
    }
    this.state.set(next);
    this.injector.get(AuditService).recordOwn(`Updated their ${SECTION_LABELS[section]}`);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  reset(section: UserSettingsSection): Observable<UserSettings> {
    return this.save(section, DEFAULT_USER_SETTINGS[section]);
  }

  /**
   * Applies "when GuestRevu opens" choices. Runs once per signed-in person per app load: reloading
   * is opening the app again, but navigating inside it is not, and re-applying on every navigation
   * would fight the user's own scope changes.
   */
  applyStartupDefaults(): void {
    const owner = this.owner();
    if (owner === ANONYMOUS_PREFERENCES_OWNER || this.startupAppliedFor === owner) return;
    this.startupAppliedFor = owner;

    const workspace = this.workspace();
    if (workspace.startupScope === 'fixed') {
      const scope = this.injector.get(ScopeService);
      const selection = workspace.startupScopeSelection;
      if (selection.kind === 'all') scope.selectAll();
      else if (selection.kind === 'view') scope.selectView(selection.id);
      else if (selection.kind === 'tag') scope.selectTag(selection.id);
      else scope.selectAccount(selection.id);
    }
    if (workspace.startupDateRange === 'fixed') {
      this.injector.get(DateFilterService).selectPreset(workspace.startupDateRangePreset);
    }
  }

  private load(owner: string): UserSettings {
    if (owner === ANONYMOUS_PREFERENCES_OWNER) return DEFAULT_USER_SETTINGS;
    try {
      const raw = this.document.defaultView?.localStorage.getItem(storageKeyFor(owner));
      return reconcileUserSettings(raw ? JSON.parse(raw) : null);
    } catch {
      return DEFAULT_USER_SETTINGS;
    }
  }
}

function storageKeyFor(owner: string): string {
  return `${USER_SETTINGS_KEY_PREFIX}:${owner}`;
}
